/**
 * ProgressBar — 3-Segment Stage Progression (DES-008)
 *
 * A thin horizontal bar at the top of the chat view showing how far
 * the conversation has progressed through the three stages.
 *
 * Segment 1 (voice): Fills over 30 seconds (warm teal)
 * Segment 2 (filtered): Fills over 60 seconds / 30s–90s (soft coral)
 * Segment 3 (reveal): Only fills when mutual reveal is accepted (warm green)
 *
 * No countdown timers or exact seconds are shown — non-anxious design.
 */

import { type Component } from "solid-js";
import type { StageManager } from "../services/stage-manager";
import styles from "./ProgressBar.module.css";

export interface ProgressBarProps {
  stageManager: StageManager;
}

const ProgressBar: Component<ProgressBarProps> = (props) => {
  const stage = () => props.stageManager.currentStage();
  const progress = () => props.stageManager.stageProgress();
  const revealState = () => props.stageManager.revealState();

  /**
   * Compute the fill width percentage for each segment.
   */
  const segment1Width = () => {
    if (stage() === 1) {
      return `${Math.min(progress() * 100, 100)}%`;
    }
    // Stage 2 or 3: segment 1 is complete
    return "100%";
  };

  const segment2Width = () => {
    if (stage() < 2) return "0%";
    if (stage() === 2) {
      return `${Math.min(progress() * 100, 100)}%`;
    }
    // Stage 3: segment 2 is complete
    return "100%";
  };

  const segment3Width = () => {
    if (stage() < 3) return "0%";
    // Stage 3 only fills when reveal is mutually accepted
    if (revealState() === "accepted") return "100%";
    return "0%";
  };

  const stageLabel = () => {
    switch (stage()) {
      case 1:
        return "Stage 1 of 3: Voice only";
      case 2:
        return "Stage 2 of 3: Filtered video";
      case 3:
        return "Stage 3 of 3: Clear video";
      default:
        return "Conversation progress";
    }
  };

  return (
    <div
      class={styles.container}
      role="progressbar"
      aria-label={stageLabel()}
      aria-valuenow={Math.round(progress() * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span class={styles.srLabel}>{stageLabel()}</span>

      {/* Segment 1: Voice only */}
      <div class={styles.segment}>
        <div
          class={`${styles.fill} ${styles.fillStage1}`}
          style={{ width: segment1Width() }}
        />
      </div>

      {/* Segment 2: Filtered video */}
      <div class={styles.segment}>
        <div
          class={`${styles.fill} ${styles.fillStage2}`}
          style={{ width: segment2Width() }}
        />
      </div>

      {/* Segment 3: Reveal / Clear video */}
      <div class={styles.segment}>
        <div
          class={`${styles.fill} ${styles.fillStage3}`}
          style={{ width: segment3Width() }}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
